/**
 * The cents-native engine.
 *
 * Same formula and same order of operations as ./legacy.ts — rooms, then the
 * frequency discount, then flexible, then promo, then add-ons, then tax. Only
 * the precision changes. Shipping a new rate card and a new arithmetic in one
 * step would make it impossible to tell a migration bug from a pricing
 * decision, so Phase 1 changes precision alone.
 */

import {
  ADD_ON_COST_CENTS,
  CleaningTypeId,
  RoomPrices,
  TAX_RATE,
  getFrequencyDiscountPercent,
  isFlexible,
} from "./rates";
import { includesFreeExtras } from "./cleaning-types";
import { parseArrival } from "./arrival";
import {
  Cents,
  applyRate,
  assertCents,
  percentDiscountCents,
  percentOfCents,
  sumCents,
} from "./money";
import {
  DayOfWeek,
  PricingConfig,
  ProximityTier,
  dayOfWeekForServiceDate,
  defaultPricingConfig,
  pricingVariesByDayOfWeek,
  TimeOfDayRule,
} from "./config";

/**
 * What a home's rooms add to its base, in cents.
 *
 * The first `bedrooms` entries of the bedroom table plus the first `bathrooms`
 * entries of the bathroom table — each room priced at its own position, so a
 * third bedroom costs what the table says a third bedroom costs.
 *
 * Refuses a count the table has no price for rather than extrapolating. Past
 * the end of the table there is no configured price, and repeating the last one
 * would be the engine inventing a rate an admin never set.
 */
export function getRoomsCents(
  prices: RoomPrices,
  bedrooms: number,
  bathrooms: number
): Cents {
  const sumFirst = (table: number[], count: number, label: string): Cents => {
    if (!Number.isInteger(count) || count < 0 || count > table.length) {
      throw new RangeError(
        `Cannot price ${count} ${label}: the room table prices 0 to ` +
          `${table.length}.`
      );
    }
    return sumCents(table.slice(0, count));
  };
  return (
    sumFirst(prices.bedroom, bedrooms, "bedrooms") +
    sumFirst(prices.bathroom, bathrooms, "bathrooms")
  );
}

/** Sales tax on a subtotal, in cents. */
export function getTaxCents(subTotalCents: Cents): Cents {
  return applyRate(subTotalCents, TAX_RATE);
}

/**
 * Add-on cost in cents.
 *
 * Note the free-extras rule is now decided by cleaning *type*, not by comparing
 * a truncated price against a threshold. The old comparison silently stops
 * working the moment the threshold carries cents.
 */
export function getAddOnsCostCents(
  cleaningType: CleaningTypeId,
  addOns: string[] | null | undefined,
  createdAt: Date
): Cents {
  const extrasIncluded = includesFreeExtras(cleaningType, createdAt);
  let cost: Cents = 0;
  for (const addOn of addOns || []) {
    const name = addOn.toLowerCase();
    if (name.includes("organizing")) cost += ADD_ON_COST_CENTS.organizing;
    else if (name.includes("laundry")) cost += ADD_ON_COST_CENTS.laundry;
    else if (name.includes("windows")) cost += ADD_ON_COST_CENTS.windows;
    else if (name.includes("cabinet")) {
      if (!extrasIncluded) cost += ADD_ON_COST_CENTS.cabinets;
    } else if (name.trim() && !extrasIncluded) {
      cost += ADD_ON_COST_CENTS.oven;
    }
  }
  return cost;
}

export interface DecimalBookingInput {
  bedrooms: string | number;
  bathrooms: string | number;
  cleaningType: CleaningTypeId;
  clean_time: string;
  /** The frequency name, e.g. "every 2 weeks". */
  frequency?: string;
  /**
   * The discount percentage, when the caller already holds it rather than the
   * name. The booking app's redux store keeps the percentage, so requiring a
   * name would mean mapping back and forth — and 10% maps to both "every 3
   * weeks" and "every 4 weeks", so that mapping is not even well defined.
   * Exactly one of `frequency` or this must be supplied.
   */
  frequencyDiscountPercent?: number;
  add_ons?: string[] | null;
  created_at: Date;
  /**
   * The calendar date the cleaning happens (`clean_date`), which decides the
   * day-of-week surge and the Same-Day surcharge. Distinct from `created_at`:
   * `created_at` picks the rate card, the service date picks the surge.
   *
   * Optional only while the config in force cannot vary by day — see
   * `pricingVariesByDayOfWeek`. Once an admin sets any surge, omitting it throws
   * rather than defaulting to a day.
   */
  service_date?: string;
  /**
   * Whether the customer took Same-Day. A runtime fact about this booking, not
   * something derivable from the config, so it is an input — see
   * `docs/adr/0002-generalize-service-price-history-for-pricing-config.md`.
   */
  same_day?: boolean;
  /**
   * The proximity tier the server resolved for this booking.
   *
   * A runtime fact like `same_day`, and for a stronger reason: it depends on
   * *other people's* bookings on the service date, which no config lookup and no
   * property of this booking can answer. Defaults to "none", so a caller that
   * knows nothing about proximity prices exactly as it did before the axis
   * existed.
   */
  proximity_tier?: ProximityTier;
}

/**
 * Resolve the day of the week to price against.
 *
 * Returns `null` when the config cannot vary by day and the caller gave no
 * service date, which is the pre-surge status quo: the landing page's "Book from
 * $X" button prices a hypothetical booking that has no service date at all.
 */
function resolveDayOfWeek(
  input: DecimalBookingInput,
  config: PricingConfig
): DayOfWeek | null {
  if (input.service_date) return dayOfWeekForServiceDate(input.service_date);
  if (input.same_day) {
    throw new RangeError(
      "A Same-Day booking must supply service_date: the Same-Day surcharge is " +
        "configured per cleaning type per day of week, and there is no day to " +
        "look up without it."
    );
  }
  if (pricingVariesByDayOfWeek(config)) {
    throw new RangeError(
      "service_date is required to price on this config — day-of-week surge, " +
        "Same-Day surcharge or a per-day Flexible Time discount is set, so the " +
        "price depends on which day the cleaning happens. Defaulting to a day " +
        "would charge one day's rate for another's booking."
    );
  }
  return null;
}

function resolveFrequencyPercent(input: DecimalBookingInput): number {
  if (input.frequencyDiscountPercent !== undefined) {
    const percent = input.frequencyDiscountPercent;
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      throw new RangeError(`frequencyDiscountPercent must be 0-100, got ${percent}`);
    }
    return percent;
  }
  if (input.frequency === undefined) {
    throw new RangeError("Supply either frequency or frequencyDiscountPercent");
  }
  return getFrequencyDiscountPercent(input.frequency);
}

export interface PriceBreakdown {
  baseCents: Cents;
  roomsCents: Cents;
  cleaningTypeCents: Cents;
  /**
   * Day-of-week surge, a percentage of `baseCents`.
   *
   * Reported as its own field but never rendered as its own line: the surge is
   * folded into the displayed appointment value (see `CONTEXT.md` and
   * `toBookingLines`). It is here because the dashboard and the pricing tests
   * need to see what was applied, not because the customer does.
   */
  dayOfWeekSurgeCents: Cents;
  /** Same-Day surcharge, a percentage of `baseCents`. Zero unless Same-Day was taken. */
  sameDaySurchargeCents: Cents;
  /**
   * The travel premium, a percentage of `baseCents`. Zero unless the booking
   * resolved to the `travel` tier.
   *
   * A separate field from the discount below rather than one signed number,
   * because the two sit on opposite sides of the subtotal: this is added into
   * gross beside the surge, and the discount is taken off and clamped. Collapsing
   * them would make a line that cannot be rendered without a branch and cannot be
   * clamped without a sign check, in a breakdown whose entire contract is that
   * its lines sum.
   */
  travelPremiumCents: Cents;
  /** The nearby-cleaning discount, a percentage of `baseCents`. */
  proximityDiscountCents: Cents;
  /**
   * A time-of-day rule that made this arrival dearer, a percentage of
   * `baseCents`. Split from the discount below for the same reason
   * `travelPremiumCents` is split from `proximityDiscountCents`: the two sit on
   * opposite sides of the subtotal, one added into gross and one clamped off it.
   */
  timeOfDayPremiumCents: Cents;
  /** A time-of-day rule that made this arrival cheaper, a percentage of `baseCents`. */
  timeOfDayDiscountCents: Cents;
  frequencyDiscountCents: Cents;
  flexibleDiscountCents: Cents;
  promoDiscountCents: Cents;
  addOnsCents: Cents;
  subTotalCents: Cents;
  taxCents: Cents;
  totalCents: Cents;
}

/**
 * The full breakdown, in cents.
 *
 * Returns every component rather than a bare total so the order summary, the
 * receipt email, Slack and the dashboard can all *render* the numbers that were
 * charged instead of each recomputing them and drifting. Five independent
 * reimplementations of this arithmetic is how the cart came to tax at 8.88%
 * while the server used 8.875%.
 */
/**
 * The signed percentage a `clean_time` earns from one day's rules.
 *
 * Throws on a rule set that cannot price an arrival unambiguously -- an empty
 * or inverted span, or two rules covering the same minute -- rather than
 * picking one and charging it. A config that says two things about one arrival
 * is an admin error, and silently taking the first would bill whichever order
 * the rows happened to be stored in.
 */
export function timeOfDayPercentFor(
  rules: TimeOfDayRule[] | undefined,
  cleanTime: string | null | undefined,
  dayOfWeek: string
): number {
  if (!Array.isArray(rules) || rules.length === 0) return 0;

  for (const rule of rules) {
    if (
      !Number.isFinite(rule.startMinutes) ||
      !Number.isFinite(rule.endMinutes) ||
      rule.endMinutes <= rule.startMinutes
    ) {
      throw new RangeError(
        `A ${dayOfWeek} time-of-day rule ends at or before it starts ` +
          `(${rule.startMinutes} to ${rule.endMinutes}). A span that covers no ` +
          `minute prices nothing, which reads as the rule simply not applying.`
      );
    }
  }
  for (let i = 0; i < rules.length; i++) {
    for (let j = i + 1; j < rules.length; j++) {
      const a = rules[i];
      const b = rules[j];
      if (a.startMinutes < b.endMinutes && b.startMinutes < a.endMinutes) {
        throw new RangeError(
          `Two ${dayOfWeek} time-of-day rules overlap (${a.startMinutes}-` +
            `${a.endMinutes} and ${b.startMinutes}-${b.endMinutes}). One arrival ` +
            `would match both, and charging either is a coin toss.`
        );
      }
    }
  }

  const arrival = parseArrival(cleanTime);
  // Flexible Time, or a time we cannot place: no rule. See the caller.
  if (!arrival || arrival.kind !== "fixed") return 0;

  const hit = rules.find(
    (rule) => arrival.minutes >= rule.startMinutes && arrival.minutes < rule.endMinutes
  );
  return hit ? hit.percent : 0;
}

export function getBookingPrice(
  input: DecimalBookingInput,
  promoDiscountCents: Cents = 0,
  /**
   * The rates in force for this booking. Defaults to the hardcoded rate card for
   * `created_at` with every surge axis at zero, so a caller that has not been
   * taught to fetch config from the API gets exactly the price it got before
   * this parameter existed.
   */
  config: PricingConfig = defaultPricingConfig(input.created_at)
): PriceBreakdown {
  const nBedrooms = Number(input.bedrooms);
  const nBathrooms = Number(input.bathrooms);
  if (!Number.isInteger(nBedrooms) || !Number.isInteger(nBathrooms)) {
    throw new RangeError("bedrooms and bathrooms must be whole numbers");
  }

  const cleaningTypeCents = config.rateCard.cleaningTypes[input.cleaningType];
  if (cleaningTypeCents === undefined) {
    throw new RangeError(`Unknown cleaning type: ${String(input.cleaningType)}`);
  }

  const flexible = isFlexible(input.clean_time);
  if (flexible && input.same_day) {
    throw new RangeError(
      "A booking cannot be both Same-Day and Flexible Time — Flexible Time is a " +
        "discount for accepting a 9am-4pm window and Same-Day offers its own " +
        "narrowing windows, so the two are mutually exclusive."
    );
  }

  const dayOfWeek = resolveDayOfWeek(input, config);

  const roomsCents = getRoomsCents(
    config.rateCard.roomCents,
    nBedrooms,
    nBathrooms
  );
  const baseCents = roomsCents + cleaningTypeCents;

  /**
   * Surge, surcharge and every discount are each a percentage of `baseCents` and
   * are then summed — never applied one after another against a running total.
   * See `docs/adr/0005-surge-and-surcharge-stack-additively.md`: 10% surge plus a
   * 15% Same-Day surcharge is 25% of base, not 15% compounded onto a surged
   * base. This is the shape the frequency and flexible discounts have always
   * had, and matching it is what keeps the breakdown's lines summable.
   */
  const dayOfWeekSurgeCents = dayOfWeek
    ? percentOfCents(
        baseCents,
        config.dayOfWeekSurgePercent[dayOfWeek],
        `${dayOfWeek} surge`
      )
    : 0;
  /**
   * The time-of-day rule this arrival falls in, as a signed percentage of base.
   *
   * Flexible Time takes none: we choose when to arrive for a flexible booking,
   * so a premium would charge the customer for a time they did not pick, and a
   * discount would be ours to give away to anyone who stopped choosing. They
   * already have the Flexible Time discount for that.
   */
  const timeOfDayPercent = dayOfWeek
    ? timeOfDayPercentFor(config.timeOfDayRules?.[dayOfWeek], input.clean_time, dayOfWeek)
    : 0;
  const timeOfDayPremiumCents =
    timeOfDayPercent > 0
      ? percentOfCents(baseCents, timeOfDayPercent, `${dayOfWeek} time-of-day premium`)
      : 0;
  const timeOfDayDiscountCents =
    timeOfDayPercent < 0
      ? percentOfCents(baseCents, -timeOfDayPercent, `${dayOfWeek} time-of-day discount`)
      : 0;

  /**
   * Pricing a Same-Day booking against a config that does not offer Same-Day on
   * that day is a bug in either direction, so it throws rather than returning a
   * surcharge of zero. On the server it means the wrong config was loaded. In the
   * browser it means the caller built a config from the public projection, which
   * deliberately carries no surcharge grid (ADR 0006) — the right move there is
   * to ask the server for a Same-Day price, not to compute one that is missing
   * the surcharge and looks like a bargain.
   */
  if (input.same_day && dayOfWeek && !config.sameDay.enabledByDay[dayOfWeek]) {
    throw new RangeError(
      `Cannot price a Same-Day booking on ${dayOfWeek}: this config does not ` +
        `offer Same-Day that day. A config built from the public projection ` +
        `never carries the Same-Day surcharge grid — resolve a Same-Day price ` +
        `server-side instead of pricing one at zero surcharge.`
    );
  }
  const sameDaySurchargeCents =
    input.same_day && dayOfWeek
      ? percentOfCents(
          baseCents,
          config.sameDaySurchargePercent[input.cleaningType]?.[dayOfWeek] ?? 0,
          `${input.cleaningType} ${dayOfWeek} Same-Day surcharge`
        )
      : 0;

  /**
   * Proximity, resolved from the tier the server handed us.
   *
   * Refuses a tier the config does not offer, in both directions and for the
   * same reasons Same-Day does. On the server it means the wrong config was
   * loaded. In the browser it means the caller built a config from the public
   * projection, which never carries proximity (ADR 0009) — and pricing a
   * discount at zero there would show the customer a total the server will not
   * honour, while pricing a *premium* at zero would undercharge outright.
   */
  const tier: ProximityTier = input.proximity_tier ?? "none";
  if (tier === "cluster" && !config.proximity.clusterEnabled) {
    throw new RangeError(
      "Cannot price a nearby-cleaning discount: this config does not offer one. " +
        "A config built from the public projection never carries proximity — " +
        "resolve the tier and its amount server-side and fold it in with " +
        "withProximityTier."
    );
  }
  if (tier === "travel" && !config.proximity.travelEnabled) {
    throw new RangeError(
      "Cannot price a travel premium: this config does not offer one. Charging " +
        "zero here would quietly drop the premium from a booking the server " +
        "resolved as needing one."
    );
  }

  const travelPremiumCents =
    tier === "travel"
      ? percentOfCents(
          baseCents,
          config.proximity.travelPremiumPercent,
          "travel premium"
        )
      : 0;

  const addOnsCents = getAddOnsCostCents(
    input.cleaningType,
    input.add_ons,
    input.created_at
  );

  /**
   * Everything the customer is being charged for, before anything is taken off.
   * Discounts are then clamped against what is left of it, in order.
   *
   * The clamping is not defensive tidiness. Without it a coupon worth more than
   * the booking — a $200 code on a $168 studio — produced a negative subtotal, a
   * negative tax and a negative total, which `toBookingLines` rendered as
   * "Total -$34.84", `linesReconcile` certified as correct, and `toStripeAmount`
   * passed straight to `paymentIntents.create({ amount: -3484 })`. An
   * admin-configurable Flexible Time discount reaches the same place by a
   * shorter route: one unit slip in the editor (1500 meaning $15, typed as
   * 150000) is a discount larger than any booking it applies to.
   *
   * `getTaskPrice` in this same file has always clamped its promo with
   * `Math.min(promo, gross)`; booking pricing simply never did.
   */
  const grossCents =
    baseCents +
    dayOfWeekSurgeCents +
    sameDaySurchargeCents +
    travelPremiumCents +
    timeOfDayPremiumCents +
    addOnsCents;

  /**
   * What is left to discount, taken down by each discount in turn. Every one is
   * clamped against it, in order, and the subtotal is whatever remains — so a
   * new discount is added in one place rather than appended to three
   * subtraction lists that must be kept in step for the lines to sum.
   */
  let remainingCents = grossCents;
  const takeOff = (cents: Cents): Cents => {
    const taken = Math.min(cents, remainingCents);
    remainingCents -= taken;
    return taken;
  };

  const frequencyDiscountCents = takeOff(
    percentDiscountCents(baseCents, resolveFrequencyPercent(input))
  );
  /**
   * `dayOfWeek` is null only when `resolveDayOfWeek` established that this
   * config cannot vary by day, which by `pricingVariesByDayOfWeek` means every
   * day carries the *same* Flexible Time discount. So reading any single day
   * here reads the right number — it is not a guess about which day it is.
   */
  const flexibleDiscountCents = takeOff(
    flexible ? config.flexibleDiscountCents[dayOfWeek ?? "sunday"] : 0
  );
  /**
   * The nearby-cleaning discount, taken off after the two the booking's own
   * shape earns and before the coupon.
   *
   * Ordered there because it belongs with them: frequency, Flexible Time and
   * proximity are all properties of the booking we are selling, while a promo
   * code is something the customer brought. Keeping the coupon clamped last
   * leaves it as the line that absorbs the shortfall on a booking whose
   * discounts exceed it, which is the behaviour already pinned by tests.
   *
   * `percentOfCents`, like surge and the travel premium: this is an
   * admin-entered percentage stored in basis points, and may carry a fraction.
   * `percentDiscountCents` is the frequency discount's whole-percent helper.
   */
  const proximityDiscountCents = takeOff(
    tier === "cluster"
      ? percentOfCents(
          baseCents,
          config.proximity.clusterDiscountPercent,
          "nearby-cleaning discount"
        )
      : 0
  );
  /**
   * Clamped like every other discount, so a rule typed as 400 instead of 4
   * cannot drive the subtotal negative. Reported as the amount actually taken.
   */
  const appliedTimeOfDayDiscountCents = takeOff(timeOfDayDiscountCents);
  /**
   * Reported as the amount actually taken off, not the amount the coupon is
   * worth, so the receipt's lines still sum to the total the card was charged.
   */
  const appliedPromoDiscountCents = takeOff(
    assertCents(promoDiscountCents, "promo discount")
  );

  const subTotalCents = remainingCents;

  const taxCents = getTaxCents(subTotalCents);

  return {
    baseCents,
    roomsCents,
    cleaningTypeCents,
    dayOfWeekSurgeCents,
    sameDaySurchargeCents,
    travelPremiumCents,
    proximityDiscountCents,
    timeOfDayPremiumCents,
    timeOfDayDiscountCents: appliedTimeOfDayDiscountCents,
    frequencyDiscountCents,
    flexibleDiscountCents,
    promoDiscountCents: appliedPromoDiscountCents,
    addOnsCents,
    subTotalCents,
    taxCents,
    totalCents: subTotalCents + taxCents,
  };
}

export interface TaskLineItem {
  service_id: string;
  price_cents: number;
  service_name?: string;
}

export interface TaskPriceBreakdown {
  subTotalCents: Cents;
  promoDiscountCents: Cents;
  taxCents: Cents;
  tipCents: Cents;
  totalCents: Cents;
  creditsAppliedCents: Cents;
  amountDueCents: Cents;
}

/**
 * The task checkout total, in cents.
 *
 * Two differences from the engine it replaces. It never converts to dollars, so
 * tax is computed on the exact cent subtotal. And it actually applies the promo
 * discount — the previous implementation accepted a `codeDiscount` argument and
 * never referenced it, so every coupon in the task flow was inert while the
 * checkout still validated the code and rejected bad ones.
 */
export function getTaskPrice(
  tasks: TaskLineItem[],
  {
    promoDiscountCents = 0,
    tipCents = 0,
    creditsCents = 0,
  }: { promoDiscountCents?: Cents; tipCents?: Cents; creditsCents?: Cents } = {}
): TaskPriceBreakdown {
  const gross = sumCents((tasks || []).map((task) => task.price_cents));
  const promo = Math.min(assertCents(promoDiscountCents, "promo discount"), gross);
  const subTotalCents = gross - promo;
  const taxCents = getTaxCents(subTotalCents);
  const totalCents = subTotalCents + taxCents + assertCents(tipCents, "tip");
  const creditsAppliedCents = Math.min(
    assertCents(creditsCents, "credits"),
    totalCents
  );
  return {
    subTotalCents,
    promoDiscountCents: promo,
    taxCents,
    tipCents,
    totalCents,
    creditsAppliedCents,
    amountDueCents: totalCents - creditsAppliedCents,
  };
}
